import { useMemo, useState } from 'react';

import { notify } from '@cast/design-system';

import {
  OptimizationType,
  PreparedWorkload,
} from 'features/workload-optimization';
import { UpdateWorkloadsError } from 'hooks/mutations/workload-optimization';
import { useUpdateWorkloadMutationV2 } from 'hooks/mutations/workload-optimization/useUpdateWorkloadV2Mutation';

import { useBatchAutoAssignmentToggle } from '../../../../_hooks/useBatchAutoAssignmentToggle';
import { useHpaV2Status, HpaV2Status } from '../../../../_hooks/useHpaV2Status';
import { useTogglePredictiveWorkloads } from '../../../../_hooks/workload/useTogglePredictiveWorkloads';
import { WorkloadOptimizationState } from '@cast/types';

import { buildWorkloadOptimizationUpdateRequests } from './utils';

type UseWorkloadsTableBulkActionsParams = {
  workloads: PreparedWorkload[];
  hpaV2Status?: HpaV2Status;
};

export const useWorkloadsTableBulkActions = ({
  workloads,
  hpaV2Status: hpaV2StatusProp,
}: UseWorkloadsTableBulkActionsParams) => {
  const { hpaV2Status: hpaV2StatusFromHook } = useHpaV2Status();
  const hpaV2Status = hpaV2StatusProp ?? hpaV2StatusFromHook;
  const { mutate } = useUpdateWorkloadMutationV2();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const selectedWorkloads = useMemo(
    () =>
      selectedIds
        .map((id) => workloads.find((w) => w.id === id))
        .filter((w): w is PreparedWorkload => !!w),
    [selectedIds, workloads]
  );

  const toggleOptimization = ({
    optimizationType,
  }: {
    optimizationType: OptimizationType;
  }) => {
    const workloadsToMutate = buildWorkloadOptimizationUpdateRequests({
      workloads,
      selectedIds,
      optimizationType,
      hpaV2Status,
    });

    mutate(workloadsToMutate, {
      onSuccess: () => {
        notify.success('Optimization setting has been changed', {
          key: 'optimization-setting-changed',
        });
      },
      onError: (error) => {
        setSelectedIds((prevIds) =>
          prevIds.filter(
            (id) => !(error as UpdateWorkloadsError).hasFailedWorkloadId(id)
          )
        );
      },
    });
  };

  const { offDisabled, onDisabled } = useMemo(() => {
    if (!workloads.length || !selectedIds.length) {
      return { offDisabled: true, onDisabled: true };
    }

    const onDisabled = selectedWorkloads.every(
      ({
        workloadConfigV2: {
          vpaConfig: { managementOption },
        },
      }) => managementOption === WorkloadOptimizationState.MANAGED
    );
    const offDisabled = onDisabled
      ? false
      : selectedWorkloads.every(
          ({
            workloadConfigV2: {
              vpaConfig: { managementOption },
            },
          }) => managementOption !== WorkloadOptimizationState.MANAGED
        );

    return { offDisabled, onDisabled };
  }, [selectedWorkloads, selectedIds.length, workloads.length]);

  const {
    enablePredictiveWorkloads,
    disablePredictiveWorkloads,
    canDisableEditableWorkloads,
    canEnableWorkloadsCount,
  } = useTogglePredictiveWorkloads({
    workloads: selectedWorkloads,
  });

  const { enableBatchAutoAssignment, canEnableAutoAssignmentCount } =
    useBatchAutoAssignmentToggle({
      workloadIds: selectedIds,
      workloads,
    });

  return {
    selectedIds,
    setSelectedIds,
    toggleOptimization,
    onDisabled,
    offDisabled,
    enablePredictiveWorkloads,
    disablePredictiveWorkloads,
    canDisableEditableWorkloads,
    canEnableWorkloadsCount,
    enableBatchAutoAssignment,
    canEnableAutoAssignmentCount,
  };
};
